import uuid from 'react-native-uuid';
import { type CreateSlice } from 'zustand-slice';

import { track } from '@lib/analytics';
import { onLogOutReset } from '@lib/analytics/mixpanel';
import Sentry from '@lib/analytics/sentry';
import AuthService, { SignupParams } from '@lib/api/AuthService';
import UserService from '@lib/api/UserService';
import { ApiError, clearSession, isApiError, setStoredSession } from '@lib/api/client';
import config from '@lib/config';
import { Address, Auth, LocalUser, ProfileInfo } from '@lib/entities';
import { DefaultLocation, Loyalty, SocialProvider, SocialProviderToken } from '@lib/entities/Auth';
import { Either } from '@lib/monad';
import { type MfaPrefill } from '@lib/utils/mfaPrefill';

import { type MfaTriggeredBy } from './Mfa';

/**
 * Pre-MFA state set on login/signup when either `requiresMfa` or `requiresPhoneVerification`
 * is true. Carries the bearer the caller needs to attach to subsequent MFA / account-verification API calls
 * and a hint about which channel(s) the backend wants verified.
 *
 * In-memory only — see plan unit U8 ("Defer-persistence" KTD + adv-1 iOS `mfa_pending`
 * persistence model). The follow-up iOS persistence work (writing this to expo-secure-store
 * under `ojs_session_pending` and restoring on cold-start) is documented in the plan as
 * part of U8 but ships as a separate follow-up commit; the in-memory state below is the
 * v1 implementation that all callers + the 428 interceptor depend on.
 */
export interface PendingMfa {
    requiresDeviceVerification: boolean;
    requiresAccVerification: boolean;
    authToken: string;
    /**
     * `login` / `signup` are the 428-gate origins (authToken is the pre-`saveAuthSession`
     * bearer). `checkout` is the mid-session origin: the user is already logged in and taps
     * "verify your account" beside the promo field, so `authToken` is the live session token
     * and resolution just refreshes profile-info (no `completeLogin`, no navigate-to-Root).
     */
    origin: 'login' | 'signup' | 'checkout';
    /**
     * Identity the login/signup 428 already carried for this unverified account (JSAD-7106),
     * used by PhoneNumberScreen / PhoneNumberModal to skip the name step and prefill the
     * number. Absent whenever the backend sent none, which is exactly the fallback case:
     * the surface prompts for whatever is missing.
     *
     * Lives here rather than on `user.phone` on purpose — see `MfaPrefill` for why seeding
     * the phone onto the placeholder user would skip the verification surface entirely.
     */
    prefill?: MfaPrefill;
    /**
     * Analytics-only (JSAD-6706): which surface started a mid-session (`origin: 'checkout'`)
     * verification — checkout nudge, rewards gate, or the Account Info banner — since `origin`
     * alone cannot tell them apart. Omitted at the login/signup 428 gate, where `origin` is the
     * answer; read through `triggeredByFromPendingMfa`.
     */
    triggeredBy?: MfaTriggeredBy;
}

/** The `triggeredBy` telemetry value for a challenge dispatched by a PhoneNumber collector. */
export const triggeredByFromPendingMfa = (pendingMfa: PendingMfa): MfaTriggeredBy =>
    pendingMfa.triggeredBy ?? pendingMfa.origin;

interface UserState {
    auth?: Auth;
    user?: LocalUser;
    addresses?: Address[];
    isLoading: boolean;
    loyalty?: Loyalty;
    defaultLocation?: DefaultLocation;
    /**
     * Pre-MFA pending state. Cleared on resolution (caller calls `clearPendingMfa()`),
     * on logout/`clearLoggedInState` (via `LoggedOutState`), and on slice reset.
     */
    pendingMfa?: PendingMfa;
}

export interface SignupContext {
    source?: string;
    store_id?: number;
}

export interface AuthState extends UserState {
    isLoading: boolean;

    socialProviderToken?: SocialProviderToken;
    captchaSignal?: string;
    captchaToken?: string;
    checkoutAfterLogin?: boolean;
    ssoProvider?: SocialProvider;
    signupContext?: SignupContext;

    /**
      @deprecated The new `user` property should be used in all cases
     */
    profileInfo?: ProfileInfo;

    setSignupContext: (ctx: SignupContext | undefined) => void;
    setPendingMfa: (pending: PendingMfa | undefined) => void;
    clearPendingMfa: VoidFunction;
    beginMfa: (params: { auth: Auth; user?: LocalUser; pendingMfa: PendingMfa }) => void;
    /**
     * Give up on a pre-login (428-gate) challenge that failed: wipe the half-authenticated
     * `auth` / `user` / `pendingMfa` set by `beginMfa` AND reset Mixpanel. The challenge
     * identified the account for its telemetry (`trackMfaEvent`), so without the reset the
     * device would keep reporting later anonymous browsing under that account.
     */
    abandonPendingMfa: VoidFunction;
    signup: (
        params: SignupParams,
        isMfaEnabled?: boolean,
        captchaToken?: string,
        isAccVerificationEnabled?: boolean
    ) => Promise<Auth | ApiError>;
    forgotPassword: (email: string) => Promise<{ email: string } | ApiError>;
    exchangeSocialToken: (
        token: SocialProviderToken,
        fname?: string,
        lname?: string | undefined,
        isMfaEnabled?: boolean,
        isAccVerificationEnabled?: boolean
    ) => Promise<Auth | ApiError>;
    exchangeOoboToken: (token: string) => Promise<Auth | ApiError>;
    clearSocialToken: VoidFunction;
    generateCaptchaSignal: VoidFunction;
    /**
     * Clears the app-level captcha slice (`captchaSignal` + `captchaToken`).
     *
     * NAMED to avoid the collision that caused a production bug: this does NOT touch the
     * hCaptcha widget. Resetting the widget so it mints a new response instead of
     * replaying its stored one is `ref.current.resetCaptcha()` in
     * `components/Captcha/index.web.tsx` (see `resetWidget`).
     */
    clearCaptchaState: VoidFunction;
    setCaptchaToken: (token: string) => void;
    setUser: (user?: LocalUser) => void;
    setAuth: (auth?: Auth) => void;
    setLoyalty: (loyalty: Loyalty) => void;
    setDefaultLocation: (defaultLocation?: DefaultLocation) => void;
    refreshProfile: () => Promise<void>;
    setAddresses: (addresses: Address[]) => void;
    setLoading: (isLoading: boolean) => void;
    setCheckoutAfterLogin: (value: boolean) => void;
    reset: VoidFunction;

    emailOptIn: () => Promise<void>;
    emailOptOut: () => Promise<void>;
}

export type PersistedAuthState = Pick<AuthState, 'auth' | 'user' | 'loyalty'>;

const LoggedOutState = {
    auth: undefined,
    user: undefined,
    addresses: undefined,
    askForNotificationPermissions: false,
    loyalty: undefined,
    defaultLocation: undefined,
    checkoutAfterLogin: false,
    pendingMfa: undefined
    // signupContext intentionally omitted — it carries QR-attribution params
    // captured in SpeedySignup before useAuth.init runs, and must survive the
    // boot-time reset that fires for anonymous visitors. It is cleared on a
    // successful signup in the slice's signup() action.
};

const DefaultAuthState: UserState = {
    ...LoggedOutState,
    isLoading: !config.e2eTesting
};

export const initAuthSlice: CreateSlice<AuthState> = (set, get, store) => {
    // Read the MFA master-flag mirror from the MFA slice (written by MfaChallengeHost).
    // Lets these non-React actions route profile-update to the v5 endpoint when MFA
    // is on, matching the React call sites that pass `isMfaEnabled` from useFeature.
    const getIsMfaEnabled = () => store.getState().mfa?.isMfaEnabled ?? false;

    /**
     * @deprecated moving this logic to useAuth hook
     */
    const setAuth = async (auth?: Auth) => {
        set({ auth });

        if (auth) {
            await setStoredSession(auth);
            return;
        }

        await clearSession();
    };

    const setEmailConsent = (emailConsent: boolean) => {
        const user = get().user;

        if (!user) {
            return;
        }

        const updated = {
            ...user,
            emailConsent
        };

        set({
            user: updated
        });
    };

    return {
        ...DefaultAuthState,
        reset: () => set({ ...LoggedOutState, ssoProvider: undefined }, 'reset'),
        setLoading: (isLoading) => set({ isLoading }, 'isLoading'),
        setAuth: (auth) => set({ auth }, 'setAuth'),
        setCheckoutAfterLogin: (value) =>
            set({ checkoutAfterLogin: value }, 'setCheckoutAfterLogin'),
        setSignupContext: (signupContext) => set({ signupContext }, 'setSignupContext'),
        setPendingMfa: (pendingMfa) => set({ pendingMfa }, 'setPendingMfa'),
        clearPendingMfa: () => set({ pendingMfa: undefined }, 'clearPendingMfa'),
        beginMfa: ({ auth, user, pendingMfa }) => set({ auth, user, pendingMfa }, 'beginMfa'),
        abandonPendingMfa: () => {
            onLogOutReset();
            set({ auth: undefined, user: undefined, pendingMfa: undefined }, 'abandonPendingMfa');
        },
        signup: async (params, isMfaEnabled, captchaToken, isAccVerificationEnabled) => {
            set({ isLoading: true });
            const ctx = get().signupContext;
            const merged: SignupParams = ctx ? { ...params, ...ctx } : params;
            const response = await AuthService.signup(merged, isMfaEnabled, captchaToken);

            if (!isApiError(response)) {
                // Account verification only counts when its flag is on — otherwise a backend
                // `requiresPhoneVerification` is ignored and signup saves the session here.
                const needsAccVerification =
                    !!isAccVerificationEnabled && response.requiresPhoneVerification;
                if (response.requiresMfa || needsAccVerification) {
                    set({ isLoading: false });
                    return response;
                }

                await setAuth(response);
                track('signup');
                set({ signupContext: undefined });
                await get().refreshProfile();
            }
            set({ isLoading: false });

            return response;
        },
        forgotPassword: async (email) => {
            set({ isLoading: true });
            const response = await AuthService.forgotPassword(email);
            set({ isLoading: false });
            track('reset password');
            return response;
        },
        refreshProfile: async () => {
            const auth = get().auth;

            if (!auth) {
                get().reset();
                return;
            }

            // Never fetch a profile against a PRE-MFA bearer — same guard as the `useAuth`
            // sibling, which is where the full rationale lives. In short: on the `login` /
            // `signup` origins the 428 gate has deliberately skipped `saveAuthSession`, and
            // `profile-info` 500s on that unverified session (Sentry JUST-SALAD-PI-17R).
            // `checkout` is excluded because it is mid-session — its bearer is the live token
            // and `finishMfaResolution` refreshes through here before clearing pendingMfa.
            const pendingOrigin = get().pendingMfa?.origin;
            if (pendingOrigin === 'login' || pendingOrigin === 'signup') {
                return;
            }

            const response = await UserService.getProfileInfo(auth.userID.toString());

            if (isApiError(response)) {
                console.error(response);
                Sentry.captureMessage('Failed to load user profile');
                return;
            }

            set(response);

            Either.bind(await UserService.fetchAddresses()).onRight((addresses) => {
                set({ addresses });
            });
        },
        setAddresses: (addresses) => set({ addresses }),
        setLoyalty: (loyalty) => set({ loyalty }),
        setUser: (user) => set({ user }),
        setDefaultLocation: (defaultLocation) => set({ defaultLocation }),
        generateCaptchaSignal: () => {
            set({ captchaSignal: uuid.v4().toString() });
        },
        clearCaptchaState: () => {
            set({ captchaSignal: undefined, captchaToken: undefined });
        },
        setCaptchaToken: (token: string) => {
            set({ captchaToken: token });
        },
        clearSocialToken: () => {
            set({ socialProviderToken: undefined });
        },
        exchangeSocialToken: async (
            token: SocialProviderToken,
            fname?: string,
            lname?: string,
            isMfaEnabled?: boolean,
            isAccVerificationEnabled?: boolean
        ) => {
            set({ isLoading: true });
            // v5 is the shared prerequisite for both verification flows (it returns the 428
            // gate and accepts the device fingerprint), so select it when EITHER flag is on —
            // matching the email/password `login`/`signup` convention.
            const useV5 = !!isMfaEnabled || !!isAccVerificationEnabled;
            const response = await AuthService.exchangeSocialProviderToken({
                socialToken: token,
                fname,
                lname,
                isMfaEnabled: useV5
            });
            set({ socialProviderToken: token });
            if (!isApiError(response)) {
                // Pending-verification gate — mirrors the `signup` action. When the backend
                // returns a challenge we must NOT save the session here; the caller reads the
                // returned response and dispatches the MFA challenge (beginMfa). Account
                // verification only counts when its flag is on.
                const needsAccVerification =
                    !!isAccVerificationEnabled && response.requiresPhoneVerification;
                if (response.requiresMfa || needsAccVerification) {
                    set({ isLoading: false });
                    return response;
                }

                await setAuth(response);
                set({ ssoProvider: token.provider }); // Store the SSO provider
                track('login'); // TODO: track social login separately?
                await get().refreshProfile();
            }
            set({ isLoading: false });

            return response;
        },
        exchangeOoboToken: async (token: string) => {
            set({ isLoading: true });
            const response = await AuthService.exchangeOoboToken(token);
            if (!isApiError(response)) {
                await setAuth({
                    ...response,
                    isImpersonated: true,
                    userIdImpersonator: response.authToken.ooboID ?? undefined
                });
                await get().refreshProfile();
            }
            set({ isLoading: false });
            return response;
        },
        emailOptIn: async () => {
            const user = get().user;
            if (!user) {
                return;
            }

            if (user.emailConsent) {
                return;
            }

            // set email consent optimistically, it will be
            // overwritten by the profile refresh
            setEmailConsent(true);

            set({
                isLoading: true
            });

            try {
                await UserService.updateUser(
                    {
                        ...user,
                        emailConsent: true
                    },
                    { isMfaEnabled: getIsMfaEnabled() }
                );

                await get().refreshProfile();
            } finally {
                set({ isLoading: false });
            }
        },
        emailOptOut: async () => {
            const user = get().user;
            if (!user) {
                return;
            }

            if (!user.emailConsent) {
                return;
            }

            setEmailConsent(false);

            set({ isLoading: true });

            try {
                await UserService.updateUser(
                    {
                        ...user,
                        emailConsent: false
                    },
                    { isMfaEnabled: getIsMfaEnabled() }
                );

                await get().refreshProfile();
            } finally {
                set({ isLoading: false });
            }
        }
    };
};

export const selectUserId = (state: AuthState): number | undefined => {
    return state.user?.id;
};

export const selectLoyalty = (state: AuthState): Loyalty | undefined => {
    return state.loyalty;
};
